import { LitElement, html, css, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { bridge } from './bridge.js';
import { initI18n, i18nReady, onI18nReady } from './i18n.js';

// Import components so they register
import './components/game-menu.js';
import './components/locked-screen.js';
import './components/completion-screen.js';
import './components/play-hud.js';

@customElement('game-overlay')
export class GameOverlay extends LitElement {
  static styles = css`
    :host {
      position: fixed;
      inset: 0;
      pointer-events: none;
      z-index: 10;
      font-family: var(--font-body);
      color: white;
    }
    .fade {
      position: absolute;
      inset: 0;
      pointer-events: none;
    }
    #power-anchor {
      position: absolute;
      bottom: env(safe-area-inset-bottom, 12px);
      left: 0;
      font-size: 1.8rem;
      font-weight: bold;
      padding: 8px 14px;
      visibility: hidden;
      pointer-events: none;
    }
  `;

  private _unsub?: () => void;
  private _unsubI18n?: () => void;
  private _resizeHandler?: () => void;

  connectedCallback() {
    super.connectedCallback();
    initI18n();
    this._unsubI18n = onI18nReady(() => this.requestUpdate());
    this._unsub = bridge.subscribe(() => {
      this.requestUpdate();
    });
    bridge.start();
    this._resizeHandler = () => this._publishAnchorPosition();
    window.addEventListener('resize', this._resizeHandler);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsub?.();
    this._unsubI18n?.();
    bridge.stop();
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler);
    }
  }

  protected firstUpdated() {
    this._publishAnchorPosition();
  }

  protected updated() {
    const w = window as any;
    const hasBottom = typeof w.__powerIconBottomPx === 'number' && w.__powerIconBottomPx >= 0;
    const hasHeight = typeof w.__powerIconHeightPx === 'number' && w.__powerIconHeightPx > 0;
    if (!hasBottom || !hasHeight) {
      this._publishAnchorPosition();
    }
  }

  /** Measure the anchor element and publish its offset for WASM power icon rendering. */
  private _publishAnchorPosition() {
    const anchor = this.shadowRoot?.getElementById('power-anchor');
    if (anchor) {
      const rect = anchor.getBoundingClientRect();
      (window as any).__powerIconBottomPx = window.innerHeight - rect.bottom;
      (window as any).__powerIconHeightPx = rect.height;
      // 3 icons + 2 gaps (gap = icon_side/8) = 3.25 × icon_side, plus margin
      const iconsRightEdge = Math.ceil(3.5 * rect.height);
      this.style.setProperty('--power-icons-right-edge', `${iconsRightEdge}px`);
    }
  }

  render() {
    if (!i18nReady()) return nothing;

    const s = bridge.state;
    const fadeAlpha = s.fadeAlpha;

    return html`
      <div id="power-anchor">?</div>
      ${this._renderScreen(s.mode)}
      ${fadeAlpha > 0
        ? html`<div class="fade" style="background: rgba(0,0,0,${fadeAlpha})"></div>`
        : nothing}
    `;
  }

  private _renderScreen(mode: string) {
    const s = bridge.state;
    switch (mode) {
      case 'menu':
        return html`<game-menu
          .menuSelection=${s.menuSelection}
          .hasSave=${s.hasSave}
        ></game-menu>`;
      case 'complete':
        return html`<completion-screen
          .isTouch=${s.isTouch}
        ></completion-screen>`;
      case 'locked':
        return html`<locked-screen></locked-screen>`;
      case 'play':
        return html`<play-hud
          .stageName=${s.stageName}
          .messageKey=${s.messageKey}
          .isTouch=${s.isTouch}
          .aspect=${s.aspect}
          .canGoPrev=${s.canGoPrev}
          .canGoNext=${s.canGoNext}
          .stageProgressed=${s.stageProgressed}
          .commentAutoShown=${s.commentAutoShown}
          .touchMenuOpen=${s.touchMenuOpen}
          .touchControlsOpen=${s.touchControlsOpen}
          .desktopControlsOpen=${s.desktopControlsOpen}
          .commentModalOpen=${s.commentModalOpen}
          .confirmReset=${s.confirmReset}
          .confirmSelection=${s.confirmSelection}
        ></play-hud>`;
      default:
        return nothing;
    }
  }
}
